import classNames from "classnames";
import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { processTypeEnum } from "../../Resource/Contents";
import {
    calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement,
    calculateMaximumIncomePaymentForFutureYearForExistingArrangement,
    calculateMaximumIncomePaymentForRemainderOfYearForExistingArrangement,
} from "./Helper/IncomePaymentHelper";
import { IncomeArrangementType, IncomeArrangementTypeMaps } from "./Resource/Content";
import { TakeFullValue } from "./TakeFullValue";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";

export interface CappedFlexiTableProps {
    data: any[];
    type: IncomeArrangementType;
    cssWrapper?: string;
    onKeyValueChange: (index: number, key: string, newValue: any) => void ;
    onValueChange:  (index: number, value: FlexiModel) => void;
    drawdown?: DrawdownModel;
    processType?: processTypeEnum | ProcessType;
    pensionProtection?: PensionProtectionDetails;
    isGADReview?: boolean;
    income?: DrawdownAnnualIncome;
}

interface Column<T = any> {
    title: string;
    key: string;
    render?: (item: T, index: number, key: string) => React.ReactNode;
}

function getColumns(props: CappedFlexiTableProps): Column[] {
    if (props.type == IncomeArrangementType.Capped) {
        const cappedColumns: Column<CappedModel>[] = [
            {
                title: "Arrangement ID",
                key: "PensionArrangementId",
            },
            {
                title: "Amount",
                key: "Amount",
                render: (item) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(item.Amount, CurrencyCode.GBP, true);
                },
            },
            {
                title: "Type",
                key: "Type",
            },
            {
                title: "GAD Maximum",
                key: "GadMaximum",
                render: (item) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(item.GadMaximum, CurrencyCode.GBP, true);
                }
            },
            {
                title: "GAD Maximum start date",
                key: "GadStartDate",
                render: (item) => {
                    return item?.GadStartDate ? JsonDateParser.getFormatDate(item.GadStartDate, "dd-MM-yyyy") : "Null";
                }
            },
            {
                title: "Taxable",
                key: "Taxable",
                render: (item) => {
                    return item.Taxable ? "Yes" : "No";
                }
            },
            {
                title: "Income taken to date",
                key: "IncomeTakenToDate",
                render: (item) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(item.IncomeTakenToDate, CurrencyCode.GBP, true);
                }},
            {
                title: "Next years GAD Maximum",
                key: "NextYearsGadMaximum",
                render: (item) => {
                    return  MoneyDisplayHelper.FromatMoneyDisplay(item?.NextYearsGadMaximum > 0 ? item?.NextYearsGadMaximum : item?.GadMaximum, CurrencyCode.GBP, true);
                }
            },
            {
                title: "Next years GAD Maximum start date",
                key: "NextGadStartDate",
                render: (item) => {
                    return item?.NextGadStartDate ? JsonDateParser.getFormatDate(item.NextGadStartDate, "dd/MM/yyyy") : "Null";
                }
            },
            {
                title: "Last mandatory review",
                key: "LastMandatoryReview",
                render: (item) => {
                    return JsonDateParser.getFormatDate(item.LastMandatoryReview, "dd-MM-yyyy")
                }
            },
            {
                title: "Maximum income payment for remainder of pension year",
                key: "MaximumIncomePaymentForRemainderOfPensionYear",
                render: (item) => {
                    const result = props ?
                        calculateMaximumIncomePaymentForRemainderOfYearForExistingArrangement(
                        props?.processType,
                        item,
                        props?.income,
                        ) :
                        item.MaximumIncomePaymentForRemainderOfPensionYear

                    return MoneyDisplayHelper.FromatMoneyDisplay(result, CurrencyCode.GBP, true);
                },
            },
            {
                title: "Maximum income payment for future pension years",
                key: "MaximumIncomePaymentForFuturePensionYears",
                render: (item) => {
                    let result = props ?
                        calculateMaximumIncomePaymentForFutureYearForExistingArrangement(
                            props?.processType,
                            item,
                            props?.income,
                        ) :
                        item?.MaximumIncomePaymentForFuturePensionYears;

                    return MoneyDisplayHelper.FromatMoneyDisplay(result, CurrencyCode.GBP, true);
                },
            },
            {
                title: "Income per payment for current pension year",
                key: "IncomePerPaymentForCurrentPensionYear",
                render: (item, index, key) => {
                    const value = item.IncomePerPaymentForCurrentPensionYear;   
                    const maxValue = calculateMaximumIncomePaymentForRemainderOfYearForExistingArrangement(
                        props?.processType,
                        item,
                        props?.income,
                    ) || item.MaximumIncomePaymentForRemainderOfPensionYear;

                    const shouldDisableCurrentYearIncome = () => {
                        if (!props.income?.NextPayment || !item.NextGadStartDate) {
                            return false;
                        }
                        
                        const nextPaymentDate = JsonDateParser.toDateNoOffset(props.income?.NextPayment);
                        const nextYearGadStartDate = JsonDateParser.toDateNoOffset(item.NextGadStartDate);
                        
                        const endOfCurrentPensionYear = new Date(nextYearGadStartDate);
                        
                        nextPaymentDate.setHours(0, 0, 0);
                        endOfCurrentPensionYear.setHours(0, 0, 0);
                        
                        return nextPaymentDate >= endOfCurrentPensionYear;
                    };

                    const isDisabled = maxValue <= 0 || shouldDisableCurrentYearIncome();

                    return (
                        <MoneyTextBoxEditor
                            cssWrapper="control-size-245"
                            value={isDisabled ? null : value}
                            currency={CurrencyCode.GBP}
                            disabled={isDisabled}
                            onChange={(newValue) => {
                                props.onKeyValueChange(index, key, newValue);
                            }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: "only allow two decimal places",
                                value: 2,
                            }}
                            validationRules={[
                                {
                                    errorMessage:
                                        "Value cannot be greater than maximum income payment for remainder of current pension year",
                                        validator: (v) => v <= maxValue,
                                },
                                {
                                    errorMessage: "Value can not be less than 0",
                                    validator: (v) => v >= 0,
                                },
                            ]}
                        />
                    );
                },
            },
            {
                title: "Income per payment for future pension years",
                key: "IncomePerPaymentForFuturePensionYears",
                render: (item, index, key) => {
                    const maxValue = props ?
                        calculateMaximumIncomePaymentForFutureYearForExistingArrangement(
                            props?.processType,
                            item,
                            props?.income,
                        ) :
                        item?.MaximumIncomePaymentForFuturePensionYears
                    return (
                        <MoneyTextBoxEditor
                            cssWrapper="control-size-245"
                            value={item.IncomePerPaymentForFuturePensionYears}
                            currency={CurrencyCode.GBP}
                            onChange={(newValue) => {
                                props.onKeyValueChange(index, key, newValue);
                            }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: "only allow two decimal places",
                                value: 2,
                            }}
                            validationRules={[
                                {
                                    errorMessage:
                                        "Value cannot be greater than maximum income payment for future pension years",
                                    validator: (v) => v <= maxValue,
                                },
                                {
                                    errorMessage: "Value can not be less than 0",
                                    validator: (v) => v >= 0,
                                },
                            ]}
                        />
                    );
                },
            },
        ];
        return cappedColumns;
    }
    const flexiColumns: Column<FlexiModel>[] = [
        {
            title: "Arrangement ID",
            key: "PensionArrangementId",
        },
        {
            title: "From",
            key: "From",
        },
        {
            title: "Amount",
            key: "Amount",
            render: (item) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(item.Amount);
            }
        },
        {
            title: "Type",
            key: "Type",
        },
        {
            title: "Maximum income payment per payment",
            key: "MaximumIncomePerPayment",
            render: (item) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement(
                    props?.processType,
                    item,
                    props.drawdown,
                    props?.pensionProtection));
            },
        },
        {
            title: "Income per payment",
            key: "IncomePerPayment",
            render: (item: FlexiModel, index, key: keyof FlexiModel) => {
                const value = item.IncomePerPayment;
                // const maxValue = item.MaximumIncomePerPayment + calculateNetDrawdownValue(props.drawdown, props.pensionProtection) || 0
                const maxValue = calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement(
                    props?.processType,
                    item,
                    props.drawdown,
                    props?.pensionProtection)
                return (
                    <TakeFullValue
                        currentValue={value}
                        maxValue={maxValue}
                        onChange={(newValue) => {
                            return props.onKeyValueChange(index, key, newValue)
                        }}
                        onTakeFullChange={(IncomePerPayment, TakeFullValue) => {
                            return props.onValueChange(index, {
                                ...item,
                                IncomePerPayment,
                                TakeFullValue
                            })
                        }}
                    />
                );
            },
        },
    ];

    return flexiColumns;
}

export const CappedFlexiTable = (props: CappedFlexiTableProps) => {
    const columns = getColumns(props);
    const cssWrapper = classNames("one-off-income-table", props.cssWrapper);

    return (
        <>
            <h3 className="mb-11">{IncomeArrangementTypeMaps[props.type]}</h3>
            <table className={cssWrapper}>
                <tbody>
                    {columns.map((row, index) => {
                        return (
                            <tr key={row.key + index}>
                                <td className="text-left"> {<h4 className="mb-11">{row.title}</h4>}</td>
                                {props.data.map((item, index) => {
                                   if (row.render) {
                                    return <td key={row.key + index}>{row.render(item, index, row.key)}</td>;
                                } else {
                                    return <td key={row.key + index}>{item[row.key]}</td>;
                                }
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};
